import React from 'react';
import { get, find, isEmpty } from 'lodash';

// React view component to render
import { personalIdWidget as PersonalIdWidgetView } from 'ui/views/widgets/personalIdWidget';
import { PersonalIdWidgetService } from 'services/personalIdWidget';
import { SERVICE_RESPONSE_STATUS, GLOBAL_LABELS, ERROR_RESPONSE_TEMPLATE, SIZES  } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { PersonalIdWidget } from 'ui/containers/PersonalIdWidget';
 *
 * // render
 * <PersonalIdWidget propName='propValue'/>
 */
export class PersonalIdWidget extends React.Component {
    
       /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind functions
        this.__bind();

        // start new service instance
        this.personalIdWidgetService = new PersonalIdWidgetService();

        // initialize state
        this.state = {
            ...props,
            isEditMode: this.checkIfEditModeOrNot( get( props, 'data.state.values', {} ) ),
            disableSubmit: false,
            closePopoverOnScrollTrigger: 0
        };
    }

    /**
     * @desc Handles binding of functions
     */
    __bind() {
        log.debug( 'PersonalIdWidget.__bind()' );
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
        this.checkIfEditModeOrNot = this.checkIfEditModeOrNot.bind( this );
        this.handleFormEdit = this.handleFormEdit.bind( this );
    }

    /**
     * @desc For calculating if we need to show edit mode or read mode
     */
    checkIfEditModeOrNot ( values ) {
        log.debug( 'PersonalIdWidget.checkIfEditModeOrNot()' );
        for( const key in values ) {
            if( !isEmpty( get( values, key, null ) ) ) {
                return false;
            }
        }
        return true;
    }

    /**
     * @desc Handles form submission
     */
    async handleFormSubmit ( formData ) {
        log.debug( 'PersonalIdWidget.handleFormSubmit()' );

        // get submit action object
        const submitAction = find( this.state.actions, { name: 'submit' } );

        // close overlay
        this.props.closeOverlay();

        // show page preloader
        pageLevelPreloader.show();
        
        // cancel ongoing service
        this.personalIdWidgetService.cancel();

        // get status of form submission
        const { status, data } = await this.personalIdWidgetService.submitFormData( {
            url: get( submitAction, 'url' ),
            data: {
                context: get( this.props, 'context' ),
                data: {
                    ...formData
                }
            }
        } );

        // hide page preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // show success overlay
            successOverlay.open( {
                message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
            } );
        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            if ( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc Handles when click on Edit Action
     */
    handleFormEdit() {
        log.debug( 'PersonalIdWidget.handleFormEdit()' );
        this.setState( {
            isEditMode: true,
        } );
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <PersonalIdWidgetView
                { ...this.state }
                onFormSubmit={ this.handleFormSubmit }
                onFormEdit = { this.handleFormEdit }
            />
        );
    }
}

// set display name
PersonalIdWidget.displayName = 'PersonalIdWidget';

// set default props
PersonalIdWidget.defaultProps = {};

// prop types
PersonalIdWidget.propTypes = {};
